import { bep20 } from '@jdiyield/abi'
import { ChainID, GAS_LIMIT } from '@jdiyield/constants'
import { Token } from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'
import { ContractSendMethod } from 'web3-eth-contract'

import getNodeUrl from './rpcRandom'

export async function makeBatchRequest(web3, calls: any[]) {
  if (calls.length === 0) return Promise.all([])
  const batch = new web3.BatchRequest()

  const promises = calls.map((call) => {
    return new Promise((resolve, reject) => {
      if (call?.request) {
        const request = call.request({}, (error, data) => {
          if (error) {
            console.log({ call, error })
            reject(error)
          } else {
            resolve(data)
          }
        })
        batch.add(request)
      } else resolve(call)
    })
  })

  batch.execute()

  const result = await Promise.allSettled(promises)
  return result.map((obj) => (obj.status === 'fulfilled' ? obj.value : null))
}

export async function makeBatchRequestIncludeError(web3, calls: any[]) {
  if (calls.length === 0) return Promise.all([])
  const batch = new web3.BatchRequest()

  const promises = calls.map((call) => {
    return new Promise((resolve) => {
      const request = call.request({}, (error, data) => {
        if (error) {
          resolve(null)
        } else {
          resolve(data)
        }
      })
      batch.add(request)
    })
  })

  batch.execute()

  return Promise.all(promises)
}

const nativeToken = {
  1: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
  56: '0xbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb',
  137: '0x0000000000000000000000000000000000001010',
  43114: 'FvwEAhmxKfeiG8SnEvq42hc6whRyY3EFYAvebMqDNDGCgxN5Z',
  250: '0x21be370d5312f44cb42ce377bc9b8a0cef1a4c83'
}
// getBalanceOf
export const balanceOf = async (
  walletAddress: string,
  contractAddress: string,
  chianId: ChainID = 56
): Promise<string> => {
  try {
    const RPC_URL = getNodeUrl(chianId)
    const web3 = new Web3(RPC_URL)
    if (Object.values(nativeToken).includes(contractAddress)) {
      const result = await web3.eth.getBalance(walletAddress)
      return result
    } else {
      const ct = new web3.eth.Contract(bep20 as any, contractAddress)
      const result = await ct.methods.balanceOf(walletAddress).call()
      return result
    }
  } catch (e) {
    return '0'
  }
}

//getAllBalancesOf
export const allBalancesOf = async (
  web3: Web3,
  walletAddress: string,
  contractList: string[],
  chainID: ChainID
): Promise<{ contract: string; balance: string }[]> => {
  try {
    const filteredContractList = contractList.filter((contract) => !Object.values(nativeToken).includes(contract))

    const nativeTokenValue = await web3.eth.getBalance(walletAddress)

    const result1 = { contract: nativeToken[chainID], balance: nativeTokenValue } as {
      contract: string
      balance: string
    }
    const result2 = (
      await makeBatchRequestIncludeError(
        web3,
        filteredContractList.map((contract) => {
          const ct = new web3.eth.Contract(bep20 as any, contract)
          return ct.methods.balanceOf(walletAddress).call
        })
      )
    ).map((balance, i) => {
      return { balance: balance, contract: filteredContractList[i] } as { contract: string; balance: string }
    })
    return [result1, ...result2].filter(({ contract }) => !!contract)
  } catch (e) {
    console.log('allBalancesOf', e)
    return []
  }
}

export const findToken = (tokens: Token[], address: string): Token | null =>
  tokens.find((token) => {
    return address.toLowerCase() === token.address.toLowerCase()
  }) ?? null

export function calculateGasMargin(value: number): number {
  // add 10%
  return new BigNumber(value)
    .times(new BigNumber(10000).plus(new BigNumber(1000)))
    .dividedBy(new BigNumber(10000))
    .integerValue()
    .toNumber()
}

export const safeGasEstimates = async ({
  contractWithMethod,
  walletAddress
}: {
  contractWithMethod: ContractSendMethod
  walletAddress: string
}): Promise<number> => {
  try {
    const result = await contractWithMethod.estimateGas({ from: walletAddress })

    return calculateGasMargin(result)
  } catch {
    return GAS_LIMIT
  }
}
